// ═══════════════════════════════════════════════════════════════
// DEBRIS ADMS — Right-of-Entry Management
// Track private property debris removal agreements, signatures, clearance
// ═══════════════════════════════════════════════════════════════

import { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import {
  Title, Text, Paper, Group, Badge, Button, TextInput, Select, Autocomplete,
  Textarea, ActionIcon, Modal, Stack, SimpleGrid, MultiSelect,
} from '../../components/gab/GabComponents';
import {
  IconSearch, IconPlus, IconPencil, IconTrash, IconRefresh,
  IconHome, IconCheck, IconMapPin,
} from '@tabler/icons-react';
import { notifications } from '../../lib/notifications';
import { useDebrisStore } from '../../store/debrisStore';
import { useAuthStore } from '../../store/authStore';
import { useModulePermission } from '../../hooks/useModulePermission';
import { fetchTable, createRecord, updateRecord, deleteRecord, TABLES, FIELDS } from '../../lib/api-switch';
import { DEBRIS_TYPE_LABELS } from '../../types/debris';
import { WORKER_URL } from '../../lib/api-switch';

function formatPhone(raw: string): string {
  const digits = raw.replace(/\D/g, '');
  if (digits.length === 10) return `(${digits.slice(0,3)}) ${digits.slice(3,6)}-${digits.slice(6)}`;
  if (digits.length === 11 && digits[0] === '1') return `(${digits.slice(1,4)}) ${digits.slice(4,7)}-${digits.slice(7)}`;
  return raw;
}

interface ROERecord {
  id: number;
  incidentId: string;
  propertyAddress: string;
  latitude: string;
  longitude: string;
  ownerName: string;
  ownerPhone: string;
  ownerEmail: string;
  ownerSignature: string;
  signedDate: string;
  status: string;
  zoneId: string;
  debrisTypes: string;
  clearedDate: string;
  clearedBy: string;
  beforePhotos: string;
  afterPhotos: string;
  notes: string;
}

const STATUS_OPTIONS = [
  { value: 'pending_signature', label: 'Pending Signature' },
  { value: 'signed', label: 'Signed — Awaiting Clearance' },
  { value: 'in_progress', label: 'Clearance In Progress' },
  { value: 'cleared', label: 'Cleared' },
  { value: 'denied', label: 'Owner Denied' },
  { value: 'void', label: 'Void' },
];

const STATUS_COLORS: Record<string, string> = {
  pending_signature: 'yellow', signed: 'blue', in_progress: 'orange',
  cleared: 'green', denied: 'red', void: 'gray',
};

const EMPTY: Partial<ROERecord> = {
  propertyAddress: '', ownerName: '', ownerPhone: '', ownerEmail: '',
  ownerSignature: '', signedDate: '', status: 'pending_signature',
  zoneId: '', debrisTypes: '[]', notes: '', latitude: '', longitude: '',
};

const TK = TABLES.debrisRightOfEntry;
const FL = FIELDS.debrisRightOfEntry;

// Compact signature pad
function SignaturePad({ label, value, onCapture }: { label: string; value?: string; onCapture: (v: string) => void }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [drawing, setDrawing] = useState(false);
  const [signed, setSigned] = useState(false);
  useEffect(() => {
    if (value && canvasRef.current) {
      const ctx = canvasRef.current.getContext('2d');
      if (ctx) {
        const img = new Image();
        img.onload = () => { ctx.clearRect(0, 0, 260, 80); ctx.drawImage(img, 0, 0, 260, 80); setSigned(true); };
        img.src = value;
      }
    }
  }, []);
  const start = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    const ctx = canvasRef.current?.getContext('2d'); if (!ctx) return;
    setDrawing(true);
    const r = canvasRef.current!.getBoundingClientRect();
    const x = 'touches' in e ? e.touches[0].clientX - r.left : e.nativeEvent.offsetX;
    const y = 'touches' in e ? e.touches[0].clientY - r.top : e.nativeEvent.offsetY;
    ctx.beginPath(); ctx.moveTo(x, y);
  }, []);
  const move = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    if (!drawing) return;
    const ctx = canvasRef.current?.getContext('2d'); if (!ctx) return;
    const r = canvasRef.current!.getBoundingClientRect();
    const x = 'touches' in e ? e.touches[0].clientX - r.left : e.nativeEvent.offsetX;
    const y = 'touches' in e ? e.touches[0].clientY - r.top : e.nativeEvent.offsetY;
    ctx.lineWidth = 2; ctx.strokeStyle = '#000'; ctx.lineCap = 'round'; ctx.lineTo(x, y); ctx.stroke();
  }, [drawing]);
  const end = useCallback(() => {
    setDrawing(false);
    if (canvasRef.current) { onCapture(canvasRef.current.toDataURL('image/png')); setSigned(true); }
  }, [onCapture]);
  return (
    <div>
      <Group justify="space-between" mb={4}>
        <Text size="xs" fw={600}>{label}</Text>
        {signed && <Badge size="xs" color="green">Signed</Badge>}
      </Group>
      <canvas ref={canvasRef} width={400} height={140}
        style={{ border: '1px solid #e5e7eb', borderRadius: 6, cursor: 'crosshair', touchAction: 'none', background: '#fff', width: '100%', maxWidth: 400 }}
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end} />
      <Button size="compact-xs" variant="subtle" mt={2} onClick={() => {
        const ctx = canvasRef.current?.getContext('2d');
        if (ctx && canvasRef.current) { ctx.clearRect(0, 0, 260, 80); setSigned(false); onCapture(''); }
      }}>Clear</Button>
    </div>
  );
}

export default function RightOfEntry() {
  const { canEdit } = useModulePermission('debris');
  const { activeIncident, token, user } = useAuthStore();
  const incidentId = activeIncident?.id?.toString() ?? '';
  const { zones, loadZones } = useDebrisStore();

  const [records, setRecords] = useState<ROERecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<ROERecord | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ROERecord | null>(null);
  const [form, setForm] = useState<Partial<ROERecord>>({ ...EMPTY });
  const [formTypes, setFormTypes] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const PAGE_SIZE = 25;
  const [page, setPage] = useState(1);

  // Address autocomplete
  const [addrSuggestions, setAddrSuggestions] = useState<{ value: string; lat: string; lng: string }[]>([]);
  const addrTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleAddrSearch = (q: string) => {
    setField('propertyAddress', q);
    if (addrTimeout.current) clearTimeout(addrTimeout.current);
    if (q.length < 3) { setAddrSuggestions([]); return; }
    addrTimeout.current = setTimeout(async () => {
      try {
        const r = await fetch(`${WORKER_URL}/api/here/autosuggest?q=${encodeURIComponent(q)}&at=33.749,-84.388&limit=5`, {
          headers: { 'Origin': 'https://apps.opengov.com' },
        });
        if (!r.ok) return;
        const data = await r.json();
        setAddrSuggestions((data.items || []).filter((i: any) => i.position).map((i: any) => ({
          value: i.title || i.address?.label || q,
          lat: String(i.position.lat), lng: String(i.position.lng),
        })));
      } catch { /* ignore */ }
    }, 300);
  };

  const loadRecords = async () => {
    if (!TK) return;
    setLoading(true);
    try {
      const rows = await fetchTable(TK, { token: token ?? undefined });
      setRecords(rows.map((r: any) => ({
        id: Number(r.record_id || r.id) || 0,
        incidentId: '',
        propertyAddress: String(r[FL.propertyAddress] || ''),
        latitude: String(r[FL.latitude] || ''),
        longitude: String(r[FL.longitude] || ''),
        ownerName: String(r[FL.ownerName] || ''),
        ownerPhone: String(r[FL.ownerPhone] || ''),
        ownerEmail: String(r[FL.ownerEmail] || ''),
        ownerSignature: String(r[FL.ownerSignature] || ''),
        signedDate: String(r[FL.signedDate] || ''),
        status: String(r[FL.status] || 'pending_signature'),
        zoneId: String(r[FL.zoneId] || ''),
        debrisTypes: String(r[FL.debrisTypes] || '[]'),
        clearedDate: String(r[FL.clearedDate] || ''),
        clearedBy: String(r[FL.clearedBy] || ''),
        beforePhotos: '',
        afterPhotos: '',
        notes: String(r[FL.notes] || ''),
      })));
    } catch { /* */ }
    setLoading(false);
  };

  useEffect(() => { loadRecords(); loadZones(); }, [loadZones]);

  const iRecords = incidentId ? records : [];
  const iZones = incidentId ? zones : [];
  const zoneSelect = useMemo(() => iZones.map(z => ({ value: String(z.id), label: z.zoneName || z.zoneCode || `#${z.id}` })), [iZones]);
  const debrisTypeOptions = Object.entries(DEBRIS_TYPE_LABELS).map(([v, l]) => ({ value: v, label: l }));

  const filtered = useMemo(() => {
    let list = iRecords;
    if (statusFilter && statusFilter !== 'all') list = list.filter(r => r.status === statusFilter);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(r =>
        r.propertyAddress.toLowerCase().includes(q) ||
        r.ownerName.toLowerCase().includes(q),
      );
    }
    return [...list].sort((a, b) => (b.id || 0) - (a.id || 0));
  }, [iRecords, statusFilter, search]);

  useEffect(() => { setPage(1); }, [filtered.length]);
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageRows = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const setField = (key: keyof ROERecord, value: string) => setForm(prev => ({ ...prev, [key]: value }));

  const openCreate = () => { setEditTarget(null); setForm({ ...EMPTY }); setFormTypes([]); setModalOpen(true); };
  const openEdit = (r: ROERecord) => {
    setEditTarget(r); setForm({ ...r });
    try { setFormTypes(JSON.parse(r.debrisTypes || '[]')); } catch { setFormTypes([]); }
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.propertyAddress?.trim() || !form.ownerName?.trim()) {
      notifications.show({ message: 'Address and owner name required', color: 'yellow' }); return;
    }
    if (!TK || !FL) return;
    setSaving(true);
    try {
      const payload: Array<[string, string]> = [
        [FL.incidentId, incidentId],
        [FL.propertyAddress, form.propertyAddress?.trim() || ''],
        [FL.latitude, form.latitude || ''],
        [FL.longitude, form.longitude || ''],
        [FL.ownerName, form.ownerName?.trim() || ''],
        [FL.ownerPhone, form.ownerPhone || ''],
        [FL.ownerEmail, form.ownerEmail || ''],
        [FL.ownerSignature, form.ownerSignature || ''],
        [FL.signedDate, form.signedDate || ''],
        [FL.status, form.status || 'pending_signature'],
      ];
      const extra: Array<[string, string]> = [
        [FL.zoneId, form.zoneId || ''],
        [FL.debrisTypes, JSON.stringify(formTypes)],
        [FL.clearedDate, form.clearedDate || ''],
        [FL.clearedBy, form.clearedBy || ''],
        [FL.notes, form.notes || ''],
      ];
      if (editTarget) {
        await updateRecord(TK, editTarget.id, payload, token ?? undefined);
        await updateRecord(TK, editTarget.id, extra, token ?? undefined);
        notifications.show({ message: 'ROE updated', color: 'green' });
      } else {
        const rid = await createRecord(TK, payload, token ?? undefined);
        if (rid) await updateRecord(TK, rid, extra, token ?? undefined);
        notifications.show({ message: 'ROE created', color: 'green' });
      }
      await loadRecords();
      setModalOpen(false);
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Save failed', color: 'red' });
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!deleteTarget || !TK) return;
    setSaving(true);
    try {
      await deleteRecord(TK, deleteTarget.id, token ?? undefined);
      notifications.show({ message: 'ROE removed', color: 'gray' });
      setDeleteTarget(null);
      await loadRecords();
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Delete failed', color: 'red' });
    } finally { setSaving(false); }
  };

  const markCleared = async (r: ROERecord) => {
    if (!TK) return;
    try {
      await updateRecord(TK, r.id, [
        [FL.status, 'cleared'],
        [FL.clearedDate, new Date().toISOString()],
        [FL.clearedBy, user?.fullName ?? user?.email ?? ''],
      ], token ?? undefined);
      notifications.show({ message: `${r.propertyAddress} marked cleared`, color: 'green' });
      await loadRecords();
    } catch { notifications.show({ message: 'Failed', color: 'red' }); }
  };

  const zoneName = (id: string) => zones.find(z => String(z.id) === id)?.zoneName ?? '';

  // Stats
  const pending = iRecords.filter(r => r.status === 'pending_signature').length;
  const signed = iRecords.filter(r => r.status === 'signed').length;
  const inProgress = iRecords.filter(r => r.status === 'in_progress').length;
  const cleared = iRecords.filter(r => r.status === 'cleared').length;

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Title order={3} c="black">Right-of-Entry</Title>
        <Group gap="xs">
          <Button size="xs" variant="light" leftSection={<IconRefresh size={14} />} loading={loading} onClick={loadRecords}>Refresh</Button>
          {canEdit && <Button size="xs" leftSection={<IconPlus size={14} />} onClick={openCreate}>New ROE</Button>}
        </Group>
      </Group>

      <SimpleGrid cols={{ base: 2, sm: 4 }}>
        <Paper p="sm" radius="md" withBorder ta="center" style={{ borderTop: '3px solid #eab308' }}>
          <Text size="xs" c="dimmed" tt="uppercase" fw={700}>Pending Signature</Text>
          <Text size="xl" fw={800}>{pending}</Text>
        </Paper>
        <Paper p="sm" radius="md" withBorder ta="center" style={{ borderTop: '3px solid #3b82f6' }}>
          <Text size="xs" c="dimmed" tt="uppercase" fw={700}>Signed</Text>
          <Text size="xl" fw={800}>{signed}</Text>
        </Paper>
        <Paper p="sm" radius="md" withBorder ta="center" style={{ borderTop: '3px solid #f97316' }}>
          <Text size="xs" c="dimmed" tt="uppercase" fw={700}>In Progress</Text>
          <Text size="xl" fw={800}>{inProgress}</Text>
        </Paper>
        <Paper p="sm" radius="md" withBorder ta="center" style={{ borderTop: '3px solid #16a34a' }}>
          <Text size="xs" c="dimmed" tt="uppercase" fw={700}>Cleared</Text>
          <Text size="xl" fw={800}>{cleared}</Text>
        </Paper>
      </SimpleGrid>

      <Group gap="sm">
        <TextInput placeholder="Search address, owner..." leftSection={<IconSearch size={14} />}
          value={search} onChange={e => setSearch(e.target.value)} size="xs" style={{ flex: 1, minWidth: 200 }} />
        <Select data={[{ value: 'all', label: 'All Statuses' }, ...STATUS_OPTIONS]}
          value={statusFilter ?? 'all'} onChange={v => setStatusFilter(v)} size="xs" w={200} />
      </Group>

      <Text size="xs" c="dimmed">{filtered.length} propert{filtered.length !== 1 ? 'ies' : 'y'}</Text>

      {filtered.length === 0 ? (
        <Paper p="xl" radius="md" withBorder style={{ textAlign: 'center' }}>
          <IconHome size={48} color="#ccc" />
          <Text c="dimmed" mt="sm">{loading ? 'Loading...' : 'No right-of-entry agreements. Create one to start tracking private property debris removal.'}</Text>
        </Paper>
      ) : (
        <Stack gap="xs">
          {pageRows.map(r => (
            <Paper key={r.id} p="md" radius="md" withBorder style={{ cursor: 'pointer' }} onClick={() => openEdit(r)}>
              <Group justify="space-between">
                <div style={{ flex: 1 }}>
                  <Group gap={8}>
                    <Text fw={600}>{r.propertyAddress || 'No address'}</Text>
                    <Badge size="sm" color={STATUS_COLORS[r.status] ?? 'gray'}>
                      {STATUS_OPTIONS.find(s => s.value === r.status)?.label ?? r.status}
                    </Badge>
                  </Group>
                  <Group gap="md" mt={4}>
                    <Text size="xs">{r.ownerName}</Text>
                    {r.ownerPhone && <Text size="xs" c="dimmed">{r.ownerPhone}</Text>}
                    {r.zoneId && <Text size="xs" c="dimmed">Zone: {zoneName(r.zoneId)}</Text>}
                    {r.signedDate && <Text size="xs" c="dimmed">Signed: {new Date(r.signedDate).toLocaleDateString()}</Text>}
                    {r.clearedDate && <Text size="xs" c="green">Cleared: {new Date(r.clearedDate).toLocaleDateString()}</Text>}
                    {r.ownerSignature && <Badge size="xs" color="green" variant="light">Signature on file</Badge>}
                  </Group>
                </div>
                <Group gap={4} onClick={e => e.stopPropagation()}>
                  {canEdit && (r.status === 'signed' || r.status === 'in_progress') && (
                    <Button size="compact-xs" variant="light" color="green"
                      leftSection={<IconCheck size={12} />}
                      onClick={() => markCleared(r)}>
                      Mark Cleared
                    </Button>
                  )}
                  {canEdit && (
                    <>
                      <ActionIcon size="sm" variant="subtle" onClick={() => openEdit(r)}>
                        <IconPencil size={14} />
                      </ActionIcon>
                      <ActionIcon size="sm" variant="subtle" color="red" onClick={() => setDeleteTarget(r)}>
                        <IconTrash size={14} />
                      </ActionIcon>
                    </>
                  )}
                </Group>
              </Group>
            </Paper>
          ))}
        </Stack>
      )}

      {totalPages > 1 && (
        <Group justify="center" mt="sm" gap="xs">
          <Button size="xs" variant="subtle" disabled={page === 1} onClick={() => setPage(1)}>««</Button>
          <Button size="xs" variant="subtle" disabled={page === 1} onClick={() => setPage(p => Math.max(1, p - 1))}>‹ Prev</Button>
          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
            const start = Math.max(1, Math.min(page - 2, totalPages - 4));
            const p2 = start + i;
            return p2 <= totalPages ? (
              <Button key={p2} size="xs" variant={p2 === page ? 'filled' : 'subtle'} onClick={() => setPage(p2)}>{p2}</Button>
            ) : null;
          })}
          <Button size="xs" variant="subtle" disabled={page === totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))}>Next ›</Button>
          <Button size="xs" variant="subtle" disabled={page === totalPages} onClick={() => setPage(totalPages)}>»»</Button>
          <Text size="xs" c="dimmed" ml="xs">{filtered.length} total</Text>
        </Group>
      )}

      {/* Create/Edit Modal */}
      <Modal opened={modalOpen} onClose={() => setModalOpen(false)}
        title={editTarget ? 'Edit Right-of-Entry' : 'New Right-of-Entry'} centered size="lg">
        <Stack gap="sm">
          <Autocomplete label="Property Address" required value={form.propertyAddress ?? ''}
            onChange={handleAddrSearch}
            onOptionSubmit={val => {
              setField('propertyAddress', val);
              const match = addrSuggestions.find(s => s.value === val);
              if (match) { setField('latitude', match.lat); setField('longitude', match.lng); }
            }}
            data={addrSuggestions.map(s => s.value)}
            placeholder="Start typing an address..." />
          <SimpleGrid cols={2}>
            <TextInput label="Latitude" value={form.latitude ?? ''}
              onChange={e => setField('latitude', e.target.value)} placeholder="33.7490" />
            <TextInput label="Longitude" value={form.longitude ?? ''}
              onChange={e => setField('longitude', e.target.value)} placeholder="-84.3880" />
          </SimpleGrid>
          <Group grow>
            <Button size="xs" variant="light" leftSection={<IconMapPin size={14} />}
              onClick={() => {
                if (!navigator.geolocation) return;
                navigator.geolocation.getCurrentPosition(
                  pos => { setField('latitude', pos.coords.latitude.toFixed(6)); setField('longitude', pos.coords.longitude.toFixed(6)); },
                  () => notifications.show({ message: 'GPS failed', color: 'red' }),
                  { enableHighAccuracy: true },
                );
              }}>
              Get GPS
            </Button>
          </Group>

          <Text size="sm" fw={600} c="dimmed">Property Owner</Text>
          <SimpleGrid cols={3}>
            <TextInput label="Owner Name" required value={form.ownerName ?? ''}
              onChange={e => setField('ownerName', e.target.value)} />
            <TextInput label="Phone" value={form.ownerPhone ?? ''}
              onChange={e => setField('ownerPhone', formatPhone(e.target.value))}
              placeholder="(404) 555-0100" />
            <TextInput label="Email" value={form.ownerEmail ?? ''}
              onChange={e => setField('ownerEmail', e.target.value)} />
          </SimpleGrid>

          <SimpleGrid cols={2}>
            <Select label="Zone" data={zoneSelect} value={form.zoneId || null}
              onChange={v => setField('zoneId', v ?? '')} searchable clearable />
            <Select label="Status" data={STATUS_OPTIONS} value={form.status || 'pending_signature'}
              onChange={v => setField('status', v ?? 'pending_signature')} />
          </SimpleGrid>

          <MultiSelect label="Debris Types" data={debrisTypeOptions}
            value={formTypes} onChange={setFormTypes} searchable />

          <SignaturePad label="Property Owner Signature"
            value={form.ownerSignature}
            onCapture={v => {
              setField('ownerSignature', v);
              if (v && !form.signedDate) setField('signedDate', new Date().toISOString());
              if (v && form.status === 'pending_signature') setField('status', 'signed');
            }} />

          <Textarea label="Notes" value={form.notes ?? ''}
            onChange={e => setField('notes', e.target.value)} autosize minRows={2} />

          <Group justify="flex-end">
            <Button variant="default" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button loading={saving} onClick={handleSave}>{editTarget ? 'Save' : 'Create ROE'}</Button>
          </Group>
        </Stack>
      </Modal>

      {/* Delete confirmation */}
      <Modal opened={!!deleteTarget} onClose={() => setDeleteTarget(null)} title="Remove ROE" centered size="sm">
        <Stack>
          <Text size="sm">Remove ROE for <strong>{deleteTarget?.propertyAddress}</strong>?</Text>
          <Group justify="flex-end">
            <Button variant="default" size="xs" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button color="red" size="xs" loading={saving} onClick={handleDelete}>Remove</Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
